import type { UUID, WidgetBootstrapResponse } from '../types/PublicBookingChannelTypes'
import { normalizePrice } from './format'
import {
  PRICE_ON_REQUEST,
  isUnconfirmedPrice,
  priceStateOf,
  type PriceState,
} from './vehiclePricing'

export interface BasketItem {
  serviceId: UUID
  name: string
  price: number | null
  durationMinutes: number | null
  /**
   * Covered by another line's price, so shown as "Included" and counted as 0:
   * the tyre fitting once a tyre is priced fully fitted (ACA-98).
   */
  included?: boolean
  /**
   * ACA-96: 'onRequest' when the garage confirms the price for this car, and
   * 'afterReg' while the car is unknown. Absent on a plain fixed price.
   */
  priceState?: PriceState
}

export interface ServiceCategory {
  name: string
  services: WidgetBootstrapResponse.ServiceItem[]
}

/**
 * Groups services into their display categories, preserving the order the
 * channel configured, and filters by a free-text search.
 *
 * Categories are matched case-insensitively but displayed using the first
 * spelling seen, so "MOT" and "mot" collapse into one group rather than
 * appearing twice.
 */
export function buildServiceCategories(
  services: WidgetBootstrapResponse.ServiceItem[],
  search = ''
): ServiceCategory[] {
  const term = search.trim().toLowerCase()
  const order: string[] = []
  const displayNames = new Map<string, string>()
  const grouped = new Map<string, WidgetBootstrapResponse.ServiceItem[]>()

  for (const service of services) {
    const display = (service.category ?? 'Other').trim().replace(/\s+/g, ' ') || 'Other'
    const key = display.toLowerCase()

    if (!grouped.has(key)) {
      grouped.set(key, [])
      displayNames.set(key, display)
      order.push(key)
    }
    grouped.get(key)?.push(service)
  }

  const categories: ServiceCategory[] = []

  for (const key of order) {
    const items = grouped.get(key) ?? []
    const sorted = [...items].sort((a, b) =>
      a.displayOrder !== b.displayOrder ? a.displayOrder - b.displayOrder : a.name.localeCompare(b.name)
    )

    const filtered = term ? sorted.filter((service) => matchesSearch(service, term)) : sorted

    if (!term || filtered.length > 0) {
      categories.push({ name: displayNames.get(key) ?? 'Other', services: filtered })
    }
  }

  return categories
}

function matchesSearch(service: WidgetBootstrapResponse.ServiceItem, term: string): boolean {
  return [service.name, service.category, service.description, ...(service.subheadings ?? [])]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
    .includes(term)
}

export function toBasketItem(service: WidgetBootstrapResponse.ServiceItem): BasketItem {
  const priceState = priceStateOf(service)
  return {
    serviceId: service.id,
    name: service.name,
    price: normalizePrice(service.price),
    durationMinutes: typeof service.durationMinutes === 'number' ? service.durationMinutes : null,
    ...(priceState === 'fixed' ? {} : { priceState }),
  }
}

/** True when any line's price is still to be confirmed: no payment is taken online then. */
export function basketHasUnconfirmedPrice(basket: BasketItem[]): boolean {
  return basket.some((item) => isUnconfirmedPrice(item.priceState))
}

export function basketTotal(basket: BasketItem[]): number {
  return basket.reduce((sum, item) => sum + (item.price ?? 0), 0)
}

/**
 * What the running total should say.
 *
 * A total is not always a number, and pretending otherwise produced the worst
 * line in the widget: a booking whose only item was a free brake check, or whose
 * real value was an unpriced tyre order, announced "Total: Free". A customer
 * reads that as "this visit costs nothing".
 *
 * So the total has states. "Free" is a badge on a line, never a total, and
 * anything we cannot price is said out loud rather than quietly left out of the
 * sum.
 */
export type TotalStatus =
  | 'empty'
  | 'priced'
  | 'free'
  | 'partial'
  | 'tyres-unpriced'
  | 'deposit'
  | 'on-request'
  | 'needs-type'

export interface TotalDisplay {
  status: TotalStatus
  /** Label for the total row, e.g. "Total" or "Due now". */
  label: string
  /** The figure itself, already formatted, e.g. "£182.85" or "£54.85 + tyres". */
  value: string
  /** Sits under the total when the figure needs explaining. Null when it doesn't. */
  caption: string | null
  /**
   * A figure shown before this one at the same size, e.g. the booking's total
   * ahead of the deposit due now (ACA-110). Absent when there is only one.
   */
  lead?: { label: string; value: string } | null
}

export interface TotalInput {
  basket: BasketItem[]
  /** Cost of a chosen tyre product, or 0 when none is priced. */
  tyreTotal: number
  /** True once a size has been chosen, whether or not a product could be priced. */
  tyresPending: boolean
  /** Deposit amount when the channel takes one, else null. */
  depositDue?: number | null
  formatMoney: (value: number) => string
}

export function describeTotal({
  basket,
  tyreTotal,
  tyresPending,
  depositDue,
  formatMoney,
}: TotalInput): TotalDisplay {
  const empty = basket.length === 0 && tyreTotal === 0 && !tyresPending
  if (empty) {
    return { status: 'empty', label: 'Total', value: '', caption: null }
  }

  // An MOT waiting for "What is it?" (ACA-110): its class sets its price, and with it what is
  // due now, so there is no total or due line to show until the customer has answered.
  if (basket.some((item) => item.priceState === 'needsType')) {
    return { status: 'needs-type', label: 'Total', value: '', caption: null }
  }

  const sum = basketTotal(basket) + tyreTotal
  // A null price is a service the garage quotes on arrival — not a zero.
  const hasUnpriced = basket.some((item) => item.price == null)
  const tyresUnpriced = tyresPending && tyreTotal === 0

  // ACA-96: the garage confirms a price on request, and nothing is paid online.
  if (basketHasUnconfirmedPrice(basket)) {
    return {
      status: 'on-request',
      label: 'Total',
      value: sum > 0 ? `from ${formatMoney(sum)}` : PRICE_ON_REQUEST,
      caption: 'The garage will confirm the price. Nothing to pay now.',
    }
  }

  // The total as plainly as what is due now (ACA-110): a deposit read on its own looked like
  // the price of the visit.
  if (depositDue != null) {
    return {
      status: 'deposit',
      label: 'Due now',
      value: formatMoney(depositDue),
      caption: 'Deposit now, balance at the garage',
      lead: { label: 'Total', value: formatMoney(sum) },
    }
  }

  if (tyresUnpriced) {
    return {
      status: 'tyres-unpriced',
      label: 'Total',
      value: sum > 0 ? `${formatMoney(sum)} + tyres` : 'Tyres + fitting',
      caption: 'Tyre prices confirmed before any work starts',
    }
  }

  if (hasUnpriced) {
    return {
      status: 'partial',
      label: 'Total',
      value: sum > 0 ? `from ${formatMoney(sum)}` : 'Quoted at the garage',
      caption: 'Some items are quoted at the garage',
    }
  }

  if (sum === 0) {
    return {
      status: 'free',
      label: 'Total',
      value: `${formatMoney(0)} to pay`,
      caption: 'No charge for this visit',
    }
  }

  return { status: 'priced', label: 'Total', value: formatMoney(sum), caption: null }
}

export function basketDuration(basket: BasketItem[]): number {
  return basket.reduce((sum, item) => sum + (item.durationMinutes ?? 0), 0)
}
